import React from "react";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "../Swiper";

import ContentCard from "../ContentCard/ContentCard";
import type {
  ContentCardData,
  ContentDetailsTab,
} from "../../@types/portalAluno";
import "swiper/swiper.min.css";
import "./styles.css";

const PRIORITY_CARDS = 4;

interface ContentCardListProps {
  title: string;
  cards: ContentCardData[];
  saved?: boolean;
  layout?: "grid" | "row";
  showViewAll?: boolean;
  viewAllLabel?: string;
  onViewAll?: () => void;
  seeMoreLabel?: string;
  onSeeMore?: () => void;
  interactive?: boolean;
  hideTitle?: boolean;
}

const ContentCardList = ({
  title,
  cards,
  saved = false,
  layout = "grid",
  showViewAll = false,
  viewAllLabel = "Ver todos",
  onViewAll,
  seeMoreLabel = "Ver todos",
  onSeeMore,
  interactive = true,
  hideTitle = false,
}: ContentCardListProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const headingId = `content-card-list-title-${React.useId()}`;
  const isRow = layout === "row";

  const { key: locationKey, pathname, search } = location;
  const handleDetailsClick = React.useCallback(
    (content: ContentCardData, tab?: ContentDetailsTab) => {
      const sourceType = content.sourceType ?? "conteudos";
      navigate(`/app/conteudo/detalhes/${sourceType}/${content.id}`, {
        state: {
          content,
          contentType: content.sourceType,
          from: `${pathname}${search}`,
          fromKey: locationKey,
          initiallySaved: saved,
          ...(tab ? { initialTab: tab } : {}),
        },
      });
    },
    [locationKey, navigate, pathname, saved, search]
  );

  return (
    <section
      className="content-card-list"
      aria-label={hideTitle ? title : undefined}
      aria-labelledby={hideTitle ? undefined : headingId}
    >
      {!hideTitle && (
        <div className="content-card-list__heading">
          <h2 id={headingId}>{title}</h2>
          {showViewAll && onViewAll && (
            <button type="button" onClick={onViewAll} aria-label={viewAllLabel}>
              <span>Ver todos</span>
              <ChevronRightRoundedIcon aria-hidden="true" />
            </button>
          )}
        </div>
      )}

      {isRow ? (
        <Swiper
          className="content-card-list__carousel"
          cssMode
          slidesPerView={1.17}
          spaceBetween={22}
          breakpoints={{
            721: { slidesPerView: 2.16, spaceBetween: 42 },
            1121: { slidesPerView: 3.18, spaceBetween: 58 },
          }}
          watchOverflow
          a11y={{
            containerMessage: `Carrossel de ${title}`,
            slideLabelMessage: "{{index}} de {{slidesLength}}",
          }}
        >
          {cards.map((card, cardIndex) => (
            <SwiperSlide key={`${card.sourceType ?? "conteudo"}-${card.id}`}>
              <ContentCard
                content={card}
                saved={saved}
                priority={cardIndex < PRIORITY_CARDS}
                onDetailsClick={
                  interactive && !card.unavailable
                    ? handleDetailsClick
                    : undefined
                }
              />
            </SwiperSlide>
          ))}

          {onSeeMore && cards.length > 0 && (
            <SwiperSlide>
              <button
                type="button"
                className="content-card-list__more"
                onClick={onSeeMore}
                aria-label={seeMoreLabel}
              >
                <span
                  className="content-card-list__more-icon"
                  aria-hidden="true"
                >
                  <AddRoundedIcon />
                </span>
                <span className="content-card-list__more-label">Ver todos</span>
              </button>
            </SwiperSlide>
          )}
        </Swiper>
      ) : (
        <div className="content-card-list__grid">
          {cards.map((card, cardIndex) => (
            <ContentCard
              key={`${card.sourceType ?? "conteudo"}-${card.id}`}
              content={card}
              saved={saved}
              priority={cardIndex < PRIORITY_CARDS}
              onDetailsClick={
                interactive && !card.unavailable
                  ? handleDetailsClick
                  : undefined
              }
            />
          ))}

          {onSeeMore && cards.length > 0 && (
            <button
              type="button"
              className="content-card-list__more"
              onClick={onSeeMore}
              aria-label={seeMoreLabel}
            >
              <span className="content-card-list__more-icon" aria-hidden="true">
                <AddRoundedIcon />
              </span>
              <span className="content-card-list__more-label">Ver todos</span>
            </button>
          )}
        </div>
      )}

      {cards.length === 0 && (
        <p className="content-card-list__empty" role="status">
          Nenhum conteúdo encontrado para os filtros selecionados.
        </p>
      )}
    </section>
  );
};

export default React.memo(ContentCardList);
